import 'react-datepicker/dist/react-datepicker.css'
import { Popover } from '@headlessui/react'
import React, { useEffect, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { Daterange } from '../../types/Daterange'
import { classNames } from '../../lib/utils/classNames'
import { HeroIcons } from '../../lib/utils/heroIcons'
import { DateFormat, formatDateString } from '../../lib/utils/formatDate'
import useLocale from '../../hooks/useLocale'
import { useTranslation } from 'next-i18next'
import { fr } from 'date-fns/locale/fr'
registerLocale('fr', fr)

interface DateRangeSelectProps {
  onDateRangeChange: (dateRange: Daterange | null) => void
  defaultDateRange?: Daterange | null
}

const DateRangeSelect = ({
  onDateRangeChange,
  defaultDateRange,
}: DateRangeSelectProps) => {
  const { t } = useTranslation('common')
  const { locale } = useLocale()

  const [startDate, setStartDate] = useState<Date | null>(
    defaultDateRange ? new Date(defaultDateRange.startAt) : null,
  )
  const [endDate, setEndDate] = useState<Date | null>(
    defaultDateRange ? new Date(defaultDateRange.endAt) : null,
  )

  useEffect(() => {
    if (defaultDateRange) {
      setStartDate(new Date(defaultDateRange.startAt))
      setEndDate(new Date(defaultDateRange.endAt))
    }
  }, [])

  const handleSelect = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)

    if (start && end) {
      onDateRangeChange({ startAt: new Date(start), endAt: new Date(end) })
    } else {
      onDateRangeChange(null)
    }
  }

  const clearSelection = () => {
    handleSelect([null, null])
  }

  return (
    <div className="relative">
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-2 text-left text-xs text-gray-700 focus:border-blue-600 sm:text-sm">
              <span
                className={classNames(
                  'whitespace-nowrap',
                  startDate && endDate ? 'text-gray-900' : 'text-gray-500',
                )}
              >
                {startDate && !endDate && (
                  <>{t('dateRangeSelect.startingAt')} </>
                )}

                {startDate &&
                  formatDateString(
                    startDate.toString(),
                    locale,
                    DateFormat.CON_PLAIN_DATE,
                  )}

                {!startDate && endDate && <>{t('dateRangeSelect.until')} </>}

                {startDate && endDate && ' - '}

                {endDate &&
                  formatDateString(
                    endDate.toString(),
                    locale,
                    DateFormat.CON_PLAIN_DATE,
                  )}

                {!startDate && !endDate && t('dateRangeSelect.selectDates')}
              </span>

              {startDate && endDate ? (
                <>
                  <span
                    className="h-5 w-5 rounded-full bg-red-600"
                    onClick={clearSelection}
                  >
                    <HeroIcons.TrashIcon
                      className="p-0.5 text-white"
                      aria-hidden="true"
                    />
                  </span>
                </>
              ) : (
                <HeroIcons.CalendarIcon
                  className="ml-1.5 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              )}
            </Popover.Button>

            <Popover.Panel className="absolute right-0 z-20 mt-1">
              <DatePicker
                selected={startDate}
                onChange={handleSelect}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                inline
                fixedHeight={false}
                showMonthDropdown
                showYearDropdown
                locale={locale}
                dateFormat="dd MMMM yyyy"
                isClearable={true}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>
    </div>
  )
}

export default DateRangeSelect
